import { ADDITIONAL_INFO_PARAM_ID, AGE_PARAM_ID, GENDER_PARAM_ID } from '@/config/constants';
import { useLogger } from '@/layers/presentation/hooks';
import { useProgressiveRerank } from '@/layers/presentation/hooks/useProgressiveRerank';
import { fetchPrograms } from '@/layers/presentation/services/programs';
import { RerankPatientParams } from '@/layers/presentation/services/rerank';
import { PatientParamValues } from '@/types/models';
import { ProgramFilters, ProgramsListResponse } from '@/types/programs';
import { useEffect, useMemo, useRef, useState } from 'react';

const DEBOUNCE_TIME = 2000;
const RECENT_PROGRAMS_LIMIT = 10;
const SEARCH_PROGRAMS_LIMIT = 25;

const LOG_MODULE = 'FE [Presentation / Hooks / ProgramsList]';

/**
 * Maps the generic patient-param selections to the rerank patient context the
 * backend expects ({ age, gender, additionalInfo }). Mirrors the extraction the
 * inline server-side rerank used so the reranker resolves the same values.
 */
function toRerankPatientParams(
  patientParams?: PatientParamValues,
): RerankPatientParams | undefined {
  if (!patientParams) return undefined;

  const first = (value: unknown): string | undefined => {
    const v = Array.isArray(value) ? value[0] : value;
    return v === undefined || v === null || v === '' ? undefined : String(v);
  };

  const ageRaw = first(patientParams[AGE_PARAM_ID]);
  const genderRaw = first(patientParams[GENDER_PARAM_ID]);
  const additionalInfoRaw = first(patientParams[ADDITIONAL_INFO_PARAM_ID]);

  const params: RerankPatientParams = {};
  if (ageRaw !== undefined && !Number.isNaN(Number(ageRaw))) params.age = Number(ageRaw);
  if (genderRaw !== undefined) params.gender = genderRaw;
  if (additionalInfoRaw !== undefined) params.additionalInfo = additionalInfoRaw;

  return Object.keys(params).length > 0 ? params : undefined;
}

function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const obj = value as Record<string, unknown>;
  return `{${Object.keys(obj)
    .sort()
    .map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(',')}}`;
}

export const useProgramsList = (
  filters: ProgramFilters,
  showDefaultRecent: boolean = true,
  ready: boolean = true,
): {
  programs: ProgramsListResponse;
  isEaLoading: boolean;
  isNonEaLoading: boolean;
  // Backward-compatible aggregate loading flag (legacy)
  isLoading: boolean;
  isInitialLoad: boolean;
  eaError: Error | null;
  nonEaError: Error | null;
  // Backward-compatible aggregate error (legacy)
  error: Error | null;
  // True while async progressive rerank is refining the displayed order.
  isRefining: boolean;
} => {
  const [programs, setPrograms] = useState<ProgramsListResponse>({
    recent: [],
    ea: [],
    nonEa: [],
  });
  const [isEaLoading, setIsEaLoading] = useState<boolean>(false);
  const [isNonEaLoading, setIsNonEaLoading] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [eaError, setEaError] = useState<Error | null>(null);
  const [nonEaError, setNonEaError] = useState<Error | null>(null);
  const eaFetchIdRef = useRef(0);
  const nonEaFetchIdRef = useRef(0);

  // Completion tracking for the settled-gate: each group records the fetch id
  // it last COMPLETED. A signature is "settled" only once the latest EA and the
  // latest non-EA fetch have both completed. Driven from the fetch `.finally`
  // callbacks (where actual completion happens) rather than a render effect, to
  // avoid reading stale loading flags during the commit that starts the fetch.
  const eaCompletedFetchIdRef = useRef(0);
  const nonEaCompletedFetchIdRef = useRef(0);
  // The signature the most recent EA+non-EA fetch pair was fired for.
  const fetchedSignatureRef = useRef<string | null>(null);
  // The signature whose EA + non-EA fetches have BOTH completed. Drives the
  // progressive-rerank gate so the rerank fires over the full result set and
  // "refining…" only shows after every group renders.
  const [settledSignature, setSettledSignature] = useState<string | null>(null);

  // Marks the fired signature settled once BOTH the latest EA and latest non-EA
  // fetches have completed. Called from each group's `.finally`; idempotent.
  const markSettledIfBothDone = () => {
    if (
      eaCompletedFetchIdRef.current === eaFetchIdRef.current &&
      nonEaCompletedFetchIdRef.current === nonEaFetchIdRef.current &&
      fetchedSignatureRef.current !== null
    ) {
      setSettledSignature(fetchedSignatureRef.current);
    }
  };

  // Rerank-relevant derivations (query + patient context). Computed before the
  // fetch effect so it can stamp the fired signature for the settled-gate.
  const trimmedQuery = filters.query?.trim() ?? '';
  const rerankPatientParams = useMemo(
    () => toRerankPatientParams(filters.patientParams),
    [filters.patientParams],
  );
  const rerankSignature = useMemo(
    () => stableStringify({ q: trimmedQuery, params: rerankPatientParams ?? null }),
    [trimmedQuery, rerankPatientParams],
  );

  const log = useLogger({
    module: LOG_MODULE,
    props: {
      filters,
      showDefaultRecent,
    },
    data: {
      values: {
        programs,
      },
      loading: {
        isEaLoading,
        isNonEaLoading,
        isInitialLoad,
      },
      error: {
        eaError,
        nonEaError,
      },
    },
  });

  useEffect(() => {
    if (!ready) {
      log('Skipping fetch - not ready due to settle gate', { ready });
      return;
    }

    const isQueryUndefined = filters.query === undefined;
    const isQueryEmpty = filters.query?.trim().length === 0;
    const hasQuery = !isQueryUndefined && !isQueryEmpty;

    const hasPatientParams =
      filters.patientParams !== undefined &&
      Object.keys(filters.patientParams).length > 0 &&
      Object.values(filters.patientParams).some(value =>
        Array.isArray(value) ? value.length > 0 : !!value,
      );

    const hasSearchCriteria = hasQuery || hasPatientParams;
    const shouldFetch = !isQueryUndefined && (hasSearchCriteria || showDefaultRecent);

    log('useEffect triggered', {
      hasQuery,
      hasPatientParams,
      hasSearchCriteria,
      showDefaultRecent,
      shouldFetch,
      filters: {
        query: filters.query,
        patientParams: filters.patientParams ? Object.keys(filters.patientParams) : null,
      },
      ready,
    });

    if (!shouldFetch) {
      log('Skipping fetch - no search criteria and showDefaultRecent=false', {
        hasSearchCriteria,
        showDefaultRecent,
        shouldFetch,
      });
      return;
    }

    // Prepare shared data
    const trimmedQueryParam = filters.query?.trim();
    const limit = hasQuery ? SEARCH_PROGRAMS_LIMIT : RECENT_PROGRAMS_LIMIT;

    // Both groups are about to (re)load for this signature: stamp it as the
    // in-flight signature and clear any prior "settled" marker so the rerank
    // gate waits for BOTH of these fetches to finish before firing.
    fetchedSignatureRef.current = rerankSignature;
    setSettledSignature(null);

    // Fire EA fetch
    const eaFilters = {
      ...filters,
      query: trimmedQueryParam,
      limit,
      isExpandedAccess: true,
    };
    const thisEaFetchId = ++eaFetchIdRef.current;
    setIsEaLoading(true);
    setEaError(null);
    fetchPrograms(eaFilters)
      .then(eaPrograms => {
        if (thisEaFetchId !== eaFetchIdRef.current) return;
        setPrograms(prev => ({ ...prev, ea: eaPrograms }));
      })
      .catch(err => {
        if (thisEaFetchId !== eaFetchIdRef.current) return;
        setEaError(err as Error);
      })
      .finally(() => {
        if (thisEaFetchId !== eaFetchIdRef.current) return;
        setIsEaLoading(false);
        eaCompletedFetchIdRef.current = thisEaFetchId;
        markSettledIfBothDone();
        if (isInitialLoad) setIsInitialLoad(false);
      });

    // Fire Non-EA fetch
    const nonEaFilters = {
      ...filters,
      query: trimmedQueryParam,
      limit,
      isExpandedAccess: false,
    };
    const thisNonEaFetchId = ++nonEaFetchIdRef.current;
    setIsNonEaLoading(true);
    setNonEaError(null);
    fetchPrograms(nonEaFilters)
      .then(nonEaPrograms => {
        if (thisNonEaFetchId !== nonEaFetchIdRef.current) return;
        setPrograms(prev => ({ ...prev, nonEa: nonEaPrograms }));
      })
      .catch(err => {
        if (thisNonEaFetchId !== nonEaFetchIdRef.current) return;
        setNonEaError(err as Error);
      })
      .finally(() => {
        if (thisNonEaFetchId !== nonEaFetchIdRef.current) return;
        setIsNonEaLoading(false);
        nonEaCompletedFetchIdRef.current = thisNonEaFetchId;
        markSettledIfBothDone();
        if (isInitialLoad) setIsInitialLoad(false);
      });
    // rerankSignature is derived purely from filters (already a dep), so it
    // only changes alongside filters; included because it's read above.
  }, [filters, showDefaultRecent, ready, rerankSignature]);

  // Reset state when we shouldn't fetch
  useEffect(() => {
    if (!ready) {
      return;
    }

    const hasQuery = !!filters.query?.trim();
    const hasPatientParams =
      !!filters.patientParams &&
      Object.values(filters.patientParams).some(value =>
        Array.isArray(value) ? value.length > 0 : !!value,
      );

    const hasSearchCriteria = hasQuery || hasPatientParams;
    const shouldFetch = hasSearchCriteria || showDefaultRecent;

    if (!shouldFetch) {
      setPrograms({ recent: [], ea: [], nonEa: [] });
      setIsEaLoading(false);
      setIsNonEaLoading(false);
      setEaError(null);
      setNonEaError(null);
      // Clear the settled marker so a prior query's "settled" state can't fire
      // a rerank over the now-empty results.
      fetchedSignatureRef.current = null;
      setSettledSignature(null);
      if (!isInitialLoad) {
        setIsInitialLoad(true);
      }
    }
  }, [filters, showDefaultRecent, isInitialLoad, ready]);

  const isLoading = isEaLoading || isNonEaLoading;
  const error = eaError || nonEaError;

  // Progressive rerank: the lists above paint at DB latency; this re-orders
  // them in place when async LLM scores arrive. Rerank only runs for real text
  // queries (matching the previous inline server-side behavior). The signature
  // ties each rerank to the settled query+filters that produced these results,
  // so a stale response from a prior query is dropped. (trimmedQuery /
  // rerankPatientParams / rerankSignature are derived above, before the fetch
  // effect, so it can stamp the in-flight signature for the settled-gate.)
  const rerankEnabled = ready && trimmedQuery.length > 0;

  const {
    ea: rerankedEa,
    nonEa: rerankedNonEa,
    isRefining,
  } = useProgressiveRerank({
    ea: programs.ea ?? [],
    nonEa: programs.nonEa ?? [],
    query: trimmedQuery,
    signature: rerankSignature,
    settledSignature,
    enabled: rerankEnabled,
    patientParams: rerankPatientParams,
  });

  const displayedPrograms = useMemo<ProgramsListResponse>(
    () => ({ ...programs, ea: rerankedEa, nonEa: rerankedNonEa }),
    [programs, rerankedEa, rerankedNonEa],
  );

  return {
    programs: displayedPrograms,
    isEaLoading,
    isNonEaLoading,
    isLoading,
    isInitialLoad,
    eaError,
    nonEaError,
    error,
    isRefining,
  };
};
